import { supabase } from "../../supabase";
import { ERROR_CODES } from "../shared/constants";

// trianing_master

/**
 * Upserts a given module into the training_master table.
 *
 * @param {object} module - The module to upsert, as an object with the following shape:
 *  - active: boolean
 *  - is_compulsory: boolean
 *  - marks: number
 *  - module_category: string
 *  - module_description: string
 *  - module_name: string
 *  - pass_threshold: number
 *  - is_deleted: boolean
 *  - training_types: number
 *  - refs_structure_level_id: number (optional)
 *  - questions: array (optional)
 *  - videos: array (optional)
 *
 * @returns {Promise<number>} - A promise resolving to 0 if the operation was successful, or 100 if an error occurred.
 */
export async function upsertTrainingMaster(module) {
  const { error, data } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("training_master")
    .upsert(module)
    .select("id");

  if (error) {
    console.error(error);
    return ERROR_CODES.DB_UPSERT_FAILED;
  }
  return { error: 0, data };
}

// delete module

/**
 * Marks a module as deleted by setting the is_deleted flag to true
 * in the "training_master" table for the given module ID.
 *
 * @param {string} moduleId - The ID of the module to be marked as deleted.
 * @returns {Promise<number>} - A promise resolving to 0 if the operation was successful, or 600 if an error occurred.
 */
export async function markTrainingAsDeleted(moduleId) {
  const { error } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("training_master")
    .update({ is_deleted: true })
    .eq("id", moduleId);

  if (error) {
    console.error(error);
    return ERROR_CODES.DB_MARK_DELETE_FAILED;
  }

  return 0;
}

// training_refs_product_type_index

/**
 * Clears all created_at dates for a given module ID in the training_refs_product_type_index table.
 * This is used when a module is being edited and the user wants to remove all product type dates.
 *
 * @param {string} moduleId - The ID of the module to clear dates for.
 * @returns {Promise<number>} - A promise resolving to 0 if the operation was successful, or 200 if an error occurred.
 */
export async function clearProductTypeIndex(moduleId) {
  const { error } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("training_refs_product_type_index")
    .update({ is_active: false })
    .eq("training_master_id", moduleId);

  if (error) {
    console.error(error);
    return ERROR_CODES.DB_INDEX_CLEAR_FAILED;
  }

  return 0;
}

/**
 * Upserts all product type dates for a given module ID in the training_refs_product_type_index table.
 *
 * @param {string} moduleId - The ID of the module to upsert dates for.
 * @param {number[]} productTypes - An array of product type IDs to insert or update.
 * @returns {Promise<number>} - A promise resolving to 0 if the operation was successful, or 300 if an error occurred.
 */
export async function upsertProductTypeIndex(moduleId, productTypes) {
  const productTypeMap = productTypes.map(pt => ({
    training_master_id: moduleId,
    refs_product_type_id: pt,
    is_active: true,
  }));

  const { error } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("training_refs_product_type_index")
    .upsert(productTypeMap);

  if (error) {
    console.error(error);
    return ERROR_CODES.DB_INDEX_UPSERT_FAILED;
  }

  return 0;
}

// trianing_supplier_index

/**
 * Clears all created_at dates for a given module ID in the training_supplier_index table.
 * This is used when a module is being edited and the user wants to remove all supplier dates.
 *
 * @param {string} moduleId - The ID of the module to clear dates for.
 * @returns {Promise<number>} - A promise resolving to 0 if the operation was successful, or 200 if an error occurred.
 */
export async function clearSupplierIndex(moduleId) {
  const { error } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("training_supplier_index")
    .update({ is_active: false })
    .eq("training_master_id", moduleId);

  if (error) {
    console.error(error);
    return ERROR_CODES.DB_INDEX_CLEAR_FAILED;
  }

  return 0;
}

/**
 * Upserts all selected suppliers for a given module ID in the training_supplier_index table.
 * If a supplier already exists, updates the created_at date to the current timestamp.
 * If a supplier does not exist, creates a new entry with the module ID and created_at date.
 *
 * @param {string} moduleId - The ID of the module to upsert dates for.
 * @param {string[]} suppliers - The IDs of the suppliers to upsert.
 * @returns {Promise<number>} - A promise resolving to 0 if the operation was successful, or 300 if an error occurred.
 */
export async function upsertSupplierIndex(moduleId, suppliers) {
  const suppliersMap = suppliers.map(supplier => ({
    training_master_id: moduleId,
    suppliers_master_id: supplier,
    is_active: true,
  }));

  const { error } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("training_supplier_index")
    .upsert(suppliersMap);

  if (error) {
    console.error(error);
    return ERROR_CODES.DB_INDEX_UPSERT_FAILED;
  }

  return 0;
}
